import { PaddedFormSectionContent } from 'modules/workflowNodeEditors/EditorBody.styled';
import { NodeEditorHeader } from 'modules/workflows/components/NodeEditor/NodeEditorHeader';
import { Experiment } from 'modules/workflows/types';
import { CampaignStateDefinition } from 'modules/graphql';
import { Spinner } from 'ui/spinner';
import { BUTTON_SIZES, RefreshButton } from 'ui/button';

import { ButtonContainer } from './ExperimentSection.styled';
import { ExperimentSectionText } from './ExperimentSectionText';
import { NewExperimentButton } from './NewExperimentButton';
import { useCampaign } from '../BaseSendEditor.hooks';

export interface ExperimentSectionProps {
  /** Campaign Id */
  campaignId?: number;
  /** Campaign Name */
  campaignName: string;
  /** Campaign State */
  campaignState?: CampaignStateDefinition;
  /** Is the campaign's delivery set to send time optimized */
  isCampaignSTOEnabled: boolean;
  /** List of experiments attached to a campaign */
  experiments: Required<Experiment>[];
  /** Is the experiment data still loading */
  isLoading: boolean;
  /** The title shown in section header */
  title?: string;
  /** Is the section disabled */
  isDisabled?: boolean;
  /** refetches the experiment object for the campaign id, */
  onRefreshClick: () => void;
}

const content = {
  noCampaignTooltip: 'This campaign must be created first before an experiment can be created.',
  templateErrorToolTipText:
    'The template attached must not have any errors before an experiment can be created',
  permissionTooltip: 'You do not have permission to create experiments',
};

const getLatestExperiment = (experiments: Required<Experiment>[]) => {
  const sortedExperiments = [...experiments].sort((a, b) => {
    return +new Date(b.createdAt) - +new Date(a.createdAt);
  });
  return sortedExperiments[0];
};

export const ExperimentSection = ({
  experiments,
  title = 'Experiment',
  campaignId,
  campaignName,
  campaignState,
  isCampaignSTOEnabled,
  isDisabled = false,
  isLoading = false,
  onRefreshClick,
}: ExperimentSectionProps) => {
  const { templateErrors } = useCampaign();

  // returns nothing if data is still loading, prevents a flash of incorrect ui
  if (isLoading) {
    return <Spinner />;
  }

  const isCampaignCreated = campaignId === 0 || !!campaignId;
  // evaluates to false for null and [] values
  const isExperimentCreated = experiments && experiments.length;
  const templateHasContentIssues = !!templateErrors.length;
  let tooltipText = '';
  if (isDisabled) {
    tooltipText = content.permissionTooltip;
  } else if (!isCampaignCreated) {
    tooltipText = content.noCampaignTooltip;
  } else if (templateHasContentIssues) {
    tooltipText = content.templateErrorToolTipText;
  }
  const isExperimentCreationDisabled = !!tooltipText.length;

  return (
    <>
      <NodeEditorHeader title={title} />
      <PaddedFormSectionContent>
        {isExperimentCreated ? (
          // if experiment exists show data on experiment
          // But, if the previous experiment has ended we want
          // to show the create experiment button as well
          <ExperimentSectionText
            experiment={getLatestExperiment(experiments)}
            campaignId={campaignId}
            campaignName={campaignName}
            campaignState={campaignState}
            isCampaignSTOEnabled={isCampaignSTOEnabled}
            isExperimentCreationDisabled={isExperimentCreationDisabled}
            tooltipText={tooltipText}
          />
        ) : (
          // if experiment does NOT exist show button to create experiment
          <ButtonContainer>
            <NewExperimentButton
              campaignId={campaignId}
              campaignName={campaignName}
              campaignState={campaignState}
              isCampaignSTOEnabled={isCampaignSTOEnabled}
              isExperimentCreationDisabled={isExperimentCreationDisabled}
              tooltipText={tooltipText}
            />
            {!isDisabled && isCampaignCreated && (
              <RefreshButton onClick={() => onRefreshClick()} size={BUTTON_SIZES.MEDIUM} />
            )}
          </ButtonContainer>
        )}
      </PaddedFormSectionContent>
    </>
  );
};

export default ExperimentSection;
